'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';

import type { ItemUI } from '@libs/common-node';
import type { DisplayStore, SearchStore } from '@libs/common-types';
import type { Nullable } from '@libs/core';

import { useUserSession } from '../hooks/useUserSession';
import {
    AccountDrawerType,
    type PageLayoutContextProps,
    type PageLayoutProviderProps,
    type SignInDrawerProps,
    type StoreDrawerOpenState,
} from '../types/pageLayout';

export const PageLayoutContext = createContext<PageLayoutContextProps | undefined>(undefined);

export function PageLayoutProvider({ children, values }: PropsWithChildren<PageLayoutProviderProps>) {
    const { storageUserSession: _, ...userSession } = useUserSession();

    const [currentVariantId, setCurrentVariantId] = useState<number>(0);

    const [currentBopisMinimumCaseQty, setCurrentBopisMinimumCaseQty] = useState<number>(0);
    const [storeDrawerOpen, setStoreDrawerOpen] = useState<StoreDrawerOpenState | null>(null);
    const [cartStoreDrawerOpen, setCartStoreDrawerOpen] = useState<{
        item: ItemUI | undefined;
        open: boolean;
    }>({
        item: undefined,
        open: false,
    });
    const [fullStoreListResults, setFullStoreListResults] = useState<SearchStore[]>([]);
    const [errorAlert, setErrorAlert] = useState<boolean>(false);

    // Store value when StoreDrawer is used in a form
    const [contactUsFormStore, setContactUsFormStore] = useState<Nullable<DisplayStore>>(null);

    const [accountDrawersOpen, setAccountDrawersOpen] = useState<AccountDrawerType[]>([]);
    const isAccountDrawerOpen = accountDrawersOpen.length > 0;

    // error alert for mismatch between SFCC Inventory and Algolia Inventory
    const [isInventoryError, setIsInventoryError] = useState<boolean>(false);

    // error alert for ATC 500 error on RC deployment
    const [refreshNeededError, setRefreshNeededError] = useState<boolean>(false);

    const [signInDrawerProps, setSignInDrawerProps] = useState<SignInDrawerProps | null>(null);

    const openAccountDrawer = useCallback((drawerId: AccountDrawerType) => {
        setAccountDrawersOpen(prev => {
            const newState = [...prev.filter(i => i !== drawerId), drawerId];
            return newState;
        });
    }, []);

    const removeAccountDrawers = useCallback((drawerIds: AccountDrawerType[]) => {
        if (drawerIds.includes(AccountDrawerType.SIGN_IN)) {
            setSignInDrawerProps(null);
        }
        setAccountDrawersOpen(prev => prev.filter(drawerId => !drawerIds.includes(drawerId)));
    }, []);

    const value = useMemo(
        () => ({
            ...values,
            ...userSession, // Pass userSession props in order to keep the changes to usePageLayout hook at minimum
            currentVariantId,
            errorAlert,
            storeDrawerOpen,
            cartStoreDrawerOpen,
            currentBopisMinimumCaseQty,
            setCurrentVariantId,
            setCurrentBopisMinimumCaseQty,
            setErrorAlert,
            setStoreDrawerOpen,
            setCartStoreDrawerOpen,
            fullStoreListResults,
            setFullStoreListResults,
            isInventoryError,
            setIsInventoryError,
            accountDrawersOpen,
            isAccountDrawerOpen,
            openAccountDrawer,
            removeAccountDrawers,
            refreshNeededError,
            setRefreshNeededError,
            signInDrawerProps,
            setSignInDrawerProps,
            contactUsFormStore,
            setContactUsFormStore,
        }),
        [
            values,
            userSession,
            errorAlert,
            storeDrawerOpen,
            cartStoreDrawerOpen,
            currentVariantId,
            fullStoreListResults,
            currentBopisMinimumCaseQty,
            isInventoryError,
            accountDrawersOpen,
            isAccountDrawerOpen,
            openAccountDrawer,
            removeAccountDrawers,
            refreshNeededError,
            signInDrawerProps,
            setSignInDrawerProps,
            contactUsFormStore,
            setContactUsFormStore,
        ]
    );

    return <PageLayoutContext.Provider value={value}>{children}</PageLayoutContext.Provider>;
}
